import { useState } from 'react';
import { useToast } from '../../context/ToastContext.jsx';

const MEDIA_TYPES = ['banner', 'video', 'native'];

/**
 * Editable floor-price row for one media type, scoped either to a publisher default or to a
 * single placement — `onSave(mediaType, {cpm, currency})` does the actual PUT so the caller can
 * point at either /api/publishers/:id/floors/:mediaType or /api/placements/:id/floors/:mediaType.
 */
export default function FloorEditor({ floors, onSave, dense }) {
  const byMediaType = {};
  (floors || []).forEach((f) => {
    byMediaType[f.media_type] = f;
  });

  return (
    <div className={`floor-editor${dense ? ' floor-editor--dense' : ''}`}>
      {MEDIA_TYPES.map((mt) => (
        <FloorField key={mt} mediaType={mt} existing={byMediaType[mt]} onSave={onSave} />
      ))}
    </div>
  );
}

function FloorField({ mediaType, existing, onSave }) {
  const toast = useToast();
  const [cpm, setCpm] = useState(existing ? String(existing.cpm) : '');
  const [currency, setCurrency] = useState(existing?.currency || 'INR');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  async function save() {
    if (!cpm || Number.isNaN(Number(cpm))) {
      toast.error('Enter a valid floor CPM.');
      return;
    }
    setSaving(true);
    try {
      await onSave(mediaType, { cpm: Number(cpm), currency });
      setDirty(false);
      toast.success(`${mediaType} floor updated.`);
    } catch (err) {
      toast.error(err.message || 'Could not update floor.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="floor-editor__row">
      <div className="floor-editor__label">{mediaType}</div>
      <input
        className="floor-editor__cpm"
        type="number"
        min="0"
        step="0.01"
        placeholder="No floor set"
        value={cpm}
        onChange={(e) => {
          setCpm(e.target.value);
          setDirty(true);
        }}
      />
      <select
        className="floor-editor__currency"
        value={currency}
        onChange={(e) => {
          setCurrency(e.target.value);
          setDirty(true);
        }}
      >
        {['INR', 'USD'].map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <button className="btn btn--ghost btn--sm" disabled={!dirty || saving} onClick={save}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
